import {
  useEffect,
  useState,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type TransitionEvent,
} from 'react';
import { cn } from '@/lib/utils';

interface FadeOutWrapperProps extends HTMLAttributes<HTMLDivElement> {
  active: boolean;
  onComplete?: () => void;
  children: ReactNode;
}

/**
 * Lightweight reusable fade-out wrapper used for exit transitions.
 */
export function FadeOutWrapper({
  active,
  onComplete,
  children,
  className,
  ...props
}: FadeOutWrapperProps): ReactElement {
  const [fadeToZero, setFadeToZero] = useState(false);

  useEffect(() => {
    if (!active) return;
    const id = requestAnimationFrame(() => {
      setFadeToZero(true);
    });
    return () => cancelAnimationFrame(id);
  }, [active]);

  const handleTransitionEnd = (_e: TransitionEvent<HTMLDivElement>): void => {
    if (!active) return;
    onComplete?.();
  };

  return (
    <div
      className={cn(
        className,
        active && 'transition-opacity duration-300 ease-out',
        active && (fadeToZero ? 'opacity-0' : 'opacity-100')
      )}
      onTransitionEnd={handleTransitionEnd}
      {...props}
    >
      {children}
    </div>
  );
}
